import { useCallback, useEffect, useRef } from 'react';
import { interval, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { LoggerService } from '@vivli/shared/infrastructure/service';
import moment from 'moment';
import {
    useAppConfigContext,
    useAuthEventsContext,
    useAuthServiceContext,
    usePortalConfigContext
} from '@vivli/core/infrastructure/context';
import { useStorage } from '@vivli/shared/infrastructure/hook';

export const useLoginExpiration = () => {
    const authService = useAuthServiceContext();
    const authEvents = useAuthEventsContext();
    const config = useAppConfigContext();
    const { getSearchLink } = usePortalConfigContext();
    const { getItem, setItem, deleteItem } = useStorage('local');
    const initRef = useRef(false);

    const expirationItemName = 'msal_timeout';
    const warningItemName = 'msal_warning';
    const intervalDelay = 5000; // 5 sec

    const warningWatcherSubRef = useRef<Subscription>();
    const expirationWatcherSubRef = useRef<Subscription>();

    const removeExpiration = useCallback(() => {
        warningWatcherSubRef.current?.unsubscribe();
        expirationWatcherSubRef.current?.unsubscribe();
        deleteItem(expirationItemName);
        deleteItem(warningItemName);
    }, [deleteItem]);

    const handleAuthExpiration = useCallback(() => {
        removeExpiration();

        if (authService.getAccount()) {
            authService.logout(getSearchLink());
        }
    }, [authService, getItem, getSearchLink, removeExpiration, setItem]);

    const setupExpirationTimer = useCallback(() => {
        LoggerService.debug('Initializing expiration timer', moment());

        expirationWatcherSubRef.current?.unsubscribe();
        // check to see if it's time to log out the user
        expirationWatcherSubRef.current = interval(intervalDelay).subscribe(() => {
            const date = getItem<string>(expirationItemName);

            const expirationDate = moment(date);
            LoggerService.debug('Checking login expiration', expirationDate);

            // check if the current time is the same or after the warning time
            if (moment().isSameOrAfter(moment(expirationDate))) {
                expirationWatcherSubRef.current?.unsubscribe();

                // log out the user
                handleAuthExpiration();
            }
        });
    }, [getItem, handleAuthExpiration]);

    const setupWarningTimer = useCallback(() => {
        warningWatcherSubRef.current?.unsubscribe();
        // check to see if it's time to warn the user they will be logged out soon
        warningWatcherSubRef.current = interval(intervalDelay).subscribe(() => {
            const date = getItem<string>(warningItemName);

            const warningDate = moment(date);
            LoggerService.debug('Checking warning expiration', warningDate);

            // check if the current time is the same or after the warning time
            if (moment().isSameOrAfter(warningDate)) {
                warningWatcherSubRef.current?.unsubscribe();

                // trigger the warning
                authEvents.next('logout-warning');

                // activate the expiration timer
                setupExpirationTimer();
            }
        });
    }, [authEvents, getItem, setupExpirationTimer]);

    const initializeExpiration = useCallback((): void => {
        // used to sync warning and timout dates
        const currentDate = moment();
        LoggerService.debug('Initializing Expiration', currentDate);

        // get minutes to expiration and minutes to warning
        const expirationMinutes = config?.sessionCookieLengthValueSeconds / 60;
        const warningMinutes = (config?.sessionCookieLengthValueSeconds - config?.sessionExpirationWarningIntervalSeconds) / 60;

        // save the date + minutes to warning/expiration to storage
        const timeTillWarning = currentDate.clone().add(warningMinutes, 'minutes').toISOString(true);
        setItem(warningItemName, timeTillWarning);

        const timeTillExpiration = currentDate.clone().add(expirationMinutes, 'minutes').toISOString(true);
        setItem(expirationItemName, timeTillExpiration);

        setupWarningTimer();
    }, [config?.sessionCookieLengthValueSeconds, config?.sessionExpirationWarningIntervalSeconds, setItem, setupWarningTimer]);

    const refreshExpiration = useCallback(() => {
        removeExpiration();
        initializeExpiration();
    }, [initializeExpiration, removeExpiration]);

    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;

        authEvents
            .pipe(
                filter((event) => event === 'token-refreshed'),
                debounceTime(1000)
            )
            .subscribe(() => {
                refreshExpiration();
            });

        authEvents.pipe(filter((event) => event !== 'token-refreshed')).subscribe((event) => {
            switch (event) {
                case 'login-success':
                case 'get-user-info-success':
                    initializeExpiration();
                    break;
                case 'logout-success':
                // The session is gone either way now, so the expiry timestamps and their timers
                // should not outlive a logout that merely failed at the B2C round-trip.
                case 'logout-failure':
                    removeExpiration();
                    break;
            }
        });

        return () => {
            removeExpiration();
        };
    }, []);
};
